import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

function JoinTeam({ user }) {
  const { t } = useTranslation('auth');
  const [teamCode, setTeamCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [hasPendingRequest, setHasPendingRequest] = useState(false);

  // Controlla se l'utente ha già una richiesta in sospeso
  useEffect(() => {
    const checkExistingRequests = async () => {
      if (!user) return;
      const q = query(collection(db, 'teamRequests'), where('userId', '==', user.uid), where('status', '==', 'pending'));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setHasPendingRequest(true);
      }
    };
    checkExistingRequests();
  }, [user]);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!teamCode.trim()) return;

    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // 1. Cerca il team con questo codice
      const q = query(collection(db, 'teams'), where('teamCode', '==', teamCode.trim().toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setMessage({ text: t('join_team.error_invalid_code'), type: 'error' });
        setIsLoading(false);
        return;
      }

      const teamDoc = querySnapshot.docs[0];
      const teamData = teamDoc.data();
      const memberCount = Object.keys(teamData.membri || {}).length;

      // 2. MAGIA PER IL TEAM PRINCIPAL (Primo accesso)
      if (memberCount === 0) {
        // Nessun membro in questo team. Il primo che usa il codice diventa Admin!
        const teamRef = doc(db, 'teams', teamDoc.id);
        await updateDoc(teamRef, {
          [`membri.${user.uid}`]: 'admin'
        });
        
        // Aggiorna l'utente
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { teamId: teamDoc.id });
        
        setMessage({ text: t('join_team.success_admin'), type: 'success' });
        // L'app reagirà automaticamente e lo porterà nella dashboard principale
      } else {
        // 3. Il team ha già un Admin. Crea una richiesta di accesso.
        await addDoc(collection(db, 'teamRequests'), {
          teamId: teamDoc.id,
          teamName: teamData.nomeTeam,
          userId: user.uid,
          email: user.email,
          status: 'pending',
          createdAt: serverTimestamp()
        });
        setHasPendingRequest(true);
        setMessage({ text: t('join_team.success_pending'), type: 'success' });
      }

    } catch (error) {
      console.error("Errore durante l'accesso al team:", error);
      setMessage({ text: t('join_team.error_generic'), type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, border: '1px solid', borderColor: 'divider', borderRadius: 3, width: '100%', maxWidth: '440px', textAlign: 'center' }}>
        
        <Box component="img" src="/MSlogo.png" alt="Logo" sx={{ height: 80, mb: 3, filter: (theme) => (theme.palette.mode === 'light' ? 'invert(1)' : 'none') }} />
        
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {t('join_team.title')}
        </Typography>

        {hasPendingRequest ? (
          <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
            {t('join_team.pending_alert')}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('join_team.subtitle')}
            </Typography>

            <Box component="form" onSubmit={handleJoin} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label={t('join_team.code_label')}
                variant="outlined"
                value={teamCode}
                onChange={(e) => setTeamCode(e.target.value)}
                disabled={isLoading}
                sx={{ mb: 3 }}
                inputProps={{ style: { textTransform: 'uppercase', letterSpacing: '2px', textAlign: 'center', fontSize: '1.2rem' } }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !teamCode.trim()}
                sx={{ height: '56px' }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : t('join_team.button')}
              </Button>
            </Box>

            {message.text && (
              <Alert severity={message.type} sx={{ mt: 3, textAlign: 'left' }}>
                {message.text}
              </Alert>
            )}
          </>
        )}
        
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="text" 
            color="inherit" 
            onClick={() => signOut(auth)}
          >
            {t('join_team.logout_button')}
          </Button>
        </Box>
        
      </Paper>
    </Box>
  );
}

export default JoinTeam;
